import IvrFlowsAPI from 'dashboard/api/ivrFlows';
import { createHandleRealtimeEvent } from './_helpers/asteriskRealtime';

export const state = {
  flows: [],
  currentFlow: null,
  meta: {
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    perPage: 25,
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getFlows: $state => $state.flows,
  getCurrentFlow: $state => $state.currentFlow,
  getMeta: $state => $state.meta,
  getUIFlags: $state => $state.uiFlags,
  getById: $state => id => $state.flows.find(f => f.id === id),
};

export const actions = {
  // Recebe broadcast ActionCable do PublishIvrFlowJob / DeleteIvrFlowAciJob.
  handleRealtimeEvent: createHandleRealtimeEvent('fetch', 'Fluxo IVR'),

  // Fetch all IVR flows
  async fetch({ commit }, { page = 1, resetList = false, platform } = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const params = { page };
      if (platform) params.platform = platform;
      const response = await IvrFlowsAPI.get(params);
      const { payload, meta } = response.data;

      if (resetList || page === 1) {
        commit('SET_FLOWS', payload);
      } else {
        commit('APPEND_FLOWS', payload);
      }
      commit('SET_META', meta);

      return payload;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  // Fetch a single flow
  async fetchById({ commit }, id) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await IvrFlowsAPI.show(id);
      commit('SET_CURRENT_FLOW', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  // Create new flow
  async create({ commit }, flowData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await IvrFlowsAPI.create(flowData);
      commit('ADD_FLOW', response.data);
      commit('SET_CURRENT_FLOW', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  // Update flow
  async update({ commit }, { id, ...data }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await IvrFlowsAPI.update(id, data);
      commit('UPDATE_FLOW', response.data);
      commit('SET_CURRENT_FLOW', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  // Delete flow
  async delete({ commit }, { id, deletePublished = false }) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await IvrFlowsAPI.delete(id, { deletePublished });
      commit('DELETE_FLOW', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  // Compile flow (preview ACI format)
  async compile({ commit }, id) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await IvrFlowsAPI.compile(id);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  // Publish flow to Asterisk ACI API
  async publish({ commit }, id) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await IvrFlowsAPI.publish(id);
      commit('SET_PUBLISHED_AT', { id, publishedAt: new Date().toISOString() });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  // Duplicate flow
  async duplicate({ commit }, id) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await IvrFlowsAPI.duplicate(id);
      commit('ADD_FLOW', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  // Set current flow
  setCurrentFlow({ commit }, flow) {
    commit('SET_CURRENT_FLOW', flow);
  },

  // Clear current flow
  clearCurrentFlow({ commit }) {
    commit('SET_CURRENT_FLOW', null);
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_FLOWS($state, flows) {
    $state.flows = flows;
  },

  APPEND_FLOWS($state, flows) {
    $state.flows = [...$state.flows, ...flows];
  },

  ADD_FLOW($state, flow) {
    $state.flows = [flow, ...$state.flows];
    $state.meta.totalCount += 1;
  },

  UPDATE_FLOW($state, flow) {
    const index = $state.flows.findIndex(f => f.id === flow.id);
    if (index !== -1) {
      $state.flows.splice(index, 1, flow);
    }
  },

  SET_PUBLISHED_AT($state, { id, publishedAt }) {
    const index = $state.flows.findIndex(f => f.id === id);
    if (index !== -1) {
      $state.flows.splice(index, 1, {
        ...$state.flows[index],
        published_at: publishedAt,
      });
    }
  },

  DELETE_FLOW($state, id) {
    $state.flows = $state.flows.filter(f => f.id !== id);
    if ($state.meta.totalCount > 0) {
      $state.meta.totalCount -= 1;
    }
    if ($state.currentFlow?.id === id) {
      $state.currentFlow = null;
    }
  },

  SET_CURRENT_FLOW($state, flow) {
    $state.currentFlow = flow;
  },

  SET_META($state, meta) {
    $state.meta = {
      totalCount: meta.total_count,
      currentPage: meta.current_page,
      totalPages: meta.total_pages,
      perPage: meta.per_page,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
